import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import classNames from "classnames";
import { useIntl } from "react-intl";
import moment from "moment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Table, OverlayTrigger, Tooltip, Form, Modal } from "react-bootstrap";
import SVG from "react-inlinesvg";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardHeader,
  CardBody,
} from "../../../_metronic/_partials/controls";
import * as actions from "../../../store/actions/index";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";

export const DatabaseManagement = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [databaseDetail, setDatabaseDetail] = useState();

  const { databaseBackups, downloadDBLink, generalSettings } = useSelector(
    (state) => state.settings
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  useEffect(() => {
    dispatch(actions.getDatabaseBackupList());
  }, [dispatch]);

  useEffect(() => {
    if (downloadDBLink) {
      if (downloadDBLink?.message) {
        alertBox(downloadDBLink.message, "error", "error", "OK");
      }
      if (downloadDBLink?.url) {
        alertBox(downloadDBLink.url, "success", "success", "Download");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadDBLink]);

  const MySwal = withReactContent(Swal);

  const alertBox = (text, icon, msgType, confirmText) => {
    MySwal.fire({
      title:
        msgType === "success" ? "Download link generated successfully." : text,
      icon,
      allowOutsideClick: false,
      allowEscapeKey: false,
      confirmButtonText: confirmText,
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(actions.clearDatabaseBackup());
        if (msgType === "success") {
          window.open(text, "_blank");
        }
        handleClose();
      }
    });
  };

  const downloadDatabase = (dbId) => {
    setShow(true);
    setDatabaseDetail(dbId);
  };

  const handleClose = () => {
    setShow(false);
    setDatabaseDetail();
    reset();
    dispatch(actions.clearDatabaseBackup());
    setLoading(false);
  };

  const onSubmitDatabaseBackup = (data) => {
    setLoading(true);
    dispatch(
      actions.downloadDatabaseBackup({
        databaseId: databaseDetail,
        password: data.password,
      })
    );
  };

  const isLoading = databaseBackups === null || databaseBackups === undefined;
  const isEmpty = Array.isArray(databaseBackups) && databaseBackups.length === 0;

  const dateFormat =
    generalSettings?.dateFormat || "YYYY-MM-DD HH:mm";

  const tableRows =
    !isLoading && !isEmpty ? (
      databaseBackups.map((backup, index) => (
        <tr key={backup.id || index}>
          <td>{index + 1}</td>
          <td>
            <span className="font-weight-bold text-dark-75">
              {backup.fileName}
            </span>
          </td>
          <td>
            <span className="label label-lg label-light-success label-inline font-weight-bold">
              Uploaded to S3
            </span>
          </td>
          <td>
            {backup.createdAt
              ? moment(backup.createdAt).format(dateFormat)
              : "—"}
          </td>
          <td>
            <OverlayTrigger
              popperConfig={{
                modifiers: {
                  preventOverflow: { enabled: false },
                  hide: { enabled: false },
                },
              }}
              placement="bottom"
              overlay={<Tooltip>Download Backup</Tooltip>}
            >
              <button
                onClick={() => downloadDatabase(backup.id)}
                className="btn btn-icon btn-light btn-hover-primary btn-sm"
              >
                <span className="svg-icon svg-icon-md svg-icon-primary">
                  <SVG
                    title=" "
                    src={toAbsoluteUrl("/media/svg/icons/Files/Download.svg")}
                  />
                </span>
              </button>
            </OverlayTrigger>
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan={5} className="text-center py-5 text-muted">
          {isLoading ? "Loading..." : "No backups found."}
        </td>
      </tr>
    );

  return (
    <>
      <Card>
        <CardHeader title="Database Backup" />
        <CardBody>
          <Table
            responsive
            bordered
            hover
            className="table-head-custom table-vertical-center"
          >
            <thead>
              <tr>
                <th style={{ width: "60px" }}>#</th>
                <th>Backup File Name</th>
                <th style={{ width: "160px" }}>S3 Status</th>
                <th style={{ width: "180px" }}>Date</th>
                <th style={{ width: "100px" }}>Action</th>
              </tr>
            </thead>
            <tbody>{tableRows}</tbody>
          </Table>
        </CardBody>
      </Card>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Download Database Backup</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Enter the backup download password to generate a secure S3 download
            link.
          </p>
          <Form
            className="form form-label-right"
            onSubmit={handleSubmit(onSubmitDatabaseBackup)}
          >
            <div className="form-group mb-4">
              <label>
                Password <span className="text-danger">*</span>
              </label>
              <Form.Control
                type="password"
                className={classNames("form-control", {
                  "is-invalid": errors.password,
                })}
                {...register("password", {
                  required: "Password is required",
                })}
              />
              <ErrorMessage
                errors={errors}
                name="password"
                render={({ message }) => (
                  <div className="invalid-feedback d-block">{message}</div>
                )}
              />
            </div>
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-light mr-3"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" />
                    Generating...
                  </>
                ) : (
                  "Generate Link"
                )}
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};
